import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  API_GET_NO_TOKEN,
  API_POST_NO_TOKEN,
  DBRequest,
  useConfig,
} from '../../api/api';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Loader from '../../components/special/Loader';
import Div100vh from 'react-div-100vh';
import { tokens } from '../../global/theme/tokens';
import { DateCalendar, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import VideocamIcon from '@mui/icons-material/Videocam';

const moment = require('moment');

const PublicBooking = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { token } = useParams();
  const config = useConfig();
  const { t } = useTranslation();

  const [bookingConfig, setBookingConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isError, setIsError] = useState(false);

  // Steps: 'category' | 'date' | 'time' | 'location' | 'form' | 'confirmation'
  const [step, setStep] = useState('category');

  // Category selection
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Slot data
  const [slots, setSlots] = useState({});
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(moment());

  // Location selection
  const [selectedLocationType, setSelectedLocationType] = useState(null);
  const [selectedLocationIndex, setSelectedLocationIndex] = useState(null);

  // Form data
  const [formData, setFormData] = useState({
    company_name: '',
    street: '',
    house_number: '',
    zip_code: '',
    city: '',
    employee_count: '',
    salutation: 'mr',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    concern: '',
    privacy_accepted: false,
    website_url: '', // honeypot
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitLoading, setSubmitLoading] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  // Load config
  useEffect(() => {
    if (!config) return;
    DBRequest({
      config,
      path: `booking/${token}`,
      method: API_GET_NO_TOKEN,
      onResponse: (res) => {
        setBookingConfig(res.data);
        // Auto-select if only one category
        if (res.data.categories?.length === 1) {
          handleCategorySelect(res.data.categories[0]);
        }
      },
      onLoading: setIsLoading,
      onError: () => setIsError(true),
    });
  }, [config]);

  // Load slots when month changes or category is selected
  useEffect(() => {
    if (!config || !bookingConfig || !selectedCategory) return;
    loadSlotsForMonth(currentMonth);
  }, [config, bookingConfig, currentMonth, selectedCategory]);

  function loadSlotsForMonth(month) {
    if (!selectedCategory) return;
    const today = moment();
    const firstOfMonth = month.clone().startOf('month');
    const startDate = (firstOfMonth.isBefore(today, 'day') ? today : firstOfMonth).format('YYYY-MM-DD');
    const endDate = month.clone().endOf('month').format('YYYY-MM-DD');

    DBRequest({
      config,
      path: `booking/${token}/slots?categoryId=${selectedCategory.id}&startDate=${startDate}&endDate=${endDate}`,
      method: API_GET_NO_TOKEN,
      onResponse: (res) =>
        setSlots((prev) => ({ ...prev, ...res.data.slots })),
      onLoading: setSlotsLoading,
    });
  }

  function handleCategorySelect(category) {
    setSelectedCategory(category);
    setSlots({});
    setSelectedDate(null);
    setSelectedSlot(null);
    setStep('date');
  }

  function handleDateSelect(date) {
    const dateStr = date.format('YYYY-MM-DD');
    const daySlots = slots[dateStr] || [];
    if (daySlots.length === 0) return;

    setSelectedDate(date);
    setSelectedSlot(null);
    setStep('time');
  }

  function handleSlotSelect(slot) {
    const dateTimeStr = `${selectedDate.format('YYYY-MM-DD')} ${slot}`;
    setSelectedSlot(dateTimeStr);

    // Determine if we need a location selection step
    const hasOnline = bookingConfig.offerOnline;
    const locations = bookingConfig.locations || [];
    const hasOnsite = locations.length > 0;

    if (hasOnline && hasOnsite) {
      // Both options available → show location step
      setStep('location');
    } else if (hasOnsite && locations.length === 1) {
      // Only one onsite location → auto-select
      setSelectedLocationType('onsite');
      setSelectedLocationIndex(0);
      setStep('form');
    } else if (hasOnsite) {
      // Multiple onsite locations, no online → show location step
      setStep('location');
    } else if (hasOnline) {
      // Only online → auto-select
      setSelectedLocationType('online');
      setSelectedLocationIndex(null);
      setStep('form');
    } else {
      // No location configured → skip to form
      setStep('form');
    }
  }

  function handleLocationSelect(type, index) {
    setSelectedLocationType(type);
    setSelectedLocationIndex(index);
    setStep('form');
  }

  function handleFormChange(key, value) {
    setFormData((prev) => ({ ...prev, [key]: value }));
    setFormErrors((prev) => ({ ...prev, [key]: undefined }));
  }

  function validateForm() {
    const errors = {};
    if (!formData.company_name.trim())
      errors.company_name = t('Is required');
    if (!formData.street.trim()) errors.street = t('Is required');
    if (!formData.house_number.trim())
      errors.house_number = t('Is required');
    if (!formData.zip_code.trim()) errors.zip_code = t('Is required');
    if (!formData.city.trim()) errors.city = t('Is required');
    if (!formData.first_name.trim()) errors.first_name = t('Is required');
    if (!formData.last_name.trim()) errors.last_name = t('Is required');
    if (
      !formData.email.trim() ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
    )
      errors.email = t('Valid email is required');
    if (!formData.phone.trim()) errors.phone = t('Is required');
    if (!formData.privacy_accepted)
      errors.privacy_accepted = t('Is required');
    return errors;
  }

  function handleSubmit() {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const submitData = {
      ...formData,
      selected_slot: selectedSlot,
      category_id: selectedCategory?.id,
      location_type: selectedLocationType || 'onsite',
      location_index: selectedLocationIndex,
    };

    DBRequest({
      config,
      path: `booking/${token}/book`,
      method: API_POST_NO_TOKEN(submitData),
      onResponse: (res) => {
        setConfirmation(res.data);
        setStep('confirmation');
        // Fire Google Ads conversion tracking
        if (bookingConfig?.gtagConversion && typeof window.gtag === 'function') {
          window.gtag('event', 'conversion', { send_to: bookingConfig.gtagConversion });
        }
      },
      onLoading: setSubmitLoading,
      onError: () =>
        setFormErrors({
          _global: t('An error occurred. Please try again.'),
        }),
    });
  }

  function shouldDisableDate(date) {
    const dateStr = date.format('YYYY-MM-DD');
    const daySlots = slots[dateStr];
    return !daySlots || daySlots.length === 0;
  }

  if (isError) {
    return (
      <Div100vh>
        <img
          src={'/assets/uploads/contact-meeting.webp'}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            position: 'absolute',
            top: '0',
            left: '0',
            objectPosition: '32%',
            zIndex: '-5',
          }}
        />
        <Box
          className="h-full flex flex-col justify-center items-center"
          sx={{ bgcolor: 'rgba(0,0,0,0.1)' }}
        >
          <Typography
            fontSize={20}
            className="px-5 py-3 rounded-lg backdrop-blur-lg"
            sx={{ bgcolor: colors.glass }}
          >
            {t('This booking page is not available')}
          </Typography>
        </Box>
      </Div100vh>
    );
  }

  return (
    <Div100vh>
      <img
        src={'/assets/uploads/contact-meeting.webp'}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          position: 'absolute',
          top: '0',
          left: '0',
          objectPosition: '32%',
          zIndex: '-5',
        }}
      />
      <Box
        className="h-full flex flex-col"
        sx={{ bgcolor: 'rgba(0,0,0,0.1)' }}
      >
        <img
          src={'/assets/svg/exopaper-text-darkmode.svg'}
          className="h-10 lg:absolute top-0 right-5 mr-auto ml-4 mt-5"
        />
        <Loader active={isLoading} />
        {bookingConfig && (
          <Box
            className="backdrop-blur-lg max-w-[600px] w-full mx-auto px-2 lg:px-4 mt-4 py-2 lg:py-3 rounded-lg lg:mt-10 h-full flex flex-col"
            sx={{
              bgcolor: colors.glass,
              maxHeight: 'calc(100% - 80px)',
            }}
          >
            {/* Header */}
            <Box className="text-center py-2">
              <Typography variant="h4" fontWeight={600} sx={{ color: colors.text }}>
                {bookingConfig.name}
              </Typography>
              {bookingConfig.description && (
                <Typography
                  variant="body2"
                  sx={{ mt: 1, opacity: 0.7, color: colors.text }}
                  dangerouslySetInnerHTML={{
                    __html: bookingConfig.description,
                  }}
                />
              )}
              {bookingConfig.appointmentDuration && (
                <Box
                  className="flex items-center justify-center gap-1 mt-2"
                  sx={{ opacity: 0.6 }}
                >
                  <AccessTimeIcon sx={{ fontSize: 16, color: colors.text }} />
                  <Typography variant="body2" sx={{ color: colors.text }}>
                    {bookingConfig.appointmentDuration} {t('Minutes')}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Content area - scrollable */}
            <Box className="flex-1 overflow-y-auto px-1 lg:px-2">
              {/* Step: Category Selection */}
              {step === 'category' && (
                <Box>
                  <Typography
                    variant="h6"
                    className="text-center mb-3"
                    sx={{ color: colors.text }}
                  >
                    {t('Select a category')}
                  </Typography>
                  <Box className="flex flex-col gap-2">
                    {(bookingConfig.categories || []).map((cat) => (
                      <Button
                        key={cat.id}
                        variant="outlined"
                        onClick={() => handleCategorySelect(cat)}
                        sx={{
                          color: colors.text,
                          borderColor: colors.bgInput,
                          bgcolor: colors.bgInput,
                          fontWeight: 600,
                          py: 1.5,
                          fontSize: '1rem',
                          '&:hover': {
                            bgcolor: colors.selected,
                            borderColor: colors.selected,
                          },
                        }}
                      >
                        {cat.label}
                      </Button>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Step: Date Selection */}
              {step === 'date' && (
                <Box>
                  <Box className="flex items-center gap-2 mb-2">
                    <Button
                      size="small"
                      startIcon={<ArrowBackIcon />}
                      onClick={() => setStep('category')}
                      sx={{ color: colors.text }}
                    >
                      {t('Back')}
                    </Button>
                    <Typography
                      variant="body2"
                      sx={{ color: colors.text, opacity: 0.7 }}
                    >
                      {selectedCategory?.label}
                    </Typography>
                  </Box>
                  <Typography
                    variant="h6"
                    className="text-center mb-2"
                    sx={{ color: colors.text }}
                  >
                    {t('Select a date')}
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterMoment}>
                    <DateCalendar
                      key={Object.keys(slots).length}
                      onChange={handleDateSelect}
                      onMonthChange={(month) => setCurrentMonth(month)}
                      shouldDisableDate={shouldDisableDate}
                      disablePast
                      maxDate={moment().add(
                        bookingConfig.advanceDays,
                        'days'
                      )}
                      loading={slotsLoading}
                      sx={{
                        width: '100%',
                        maxHeight: 340,
                        bgcolor: 'transparent',
                        '& .MuiPickersCalendarHeader-root': {
                          px: 1,
                          mb: 0.5,
                          bgcolor: 'transparent',
                          backdropFilter: 'none',
                        },
                        '& .MuiPickersCalendarHeader-label': {
                          color: colors.text,
                          fontSize: '0.95rem',
                          fontWeight: 600,
                          letterSpacing: '0.02em',
                        },
                        '& .MuiPickersArrowSwitcher-button': {
                          color: colors.text,
                          opacity: 0.6,
                          bgcolor: 'transparent',
                          '&:hover': {
                            opacity: 1,
                            bgcolor: 'transparent',
                          },
                        },
                        '& .MuiDayCalendar-weekDayLabel': {
                          color: colors.text,
                          opacity: 0.4,
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          letterSpacing: '0.05em',
                        },
                        '& .MuiPickersDay-root': {
                          color: colors.text,
                          fontSize: '0.85rem',
                          borderRadius: '8px',
                          transition: 'all 0.15s ease',
                          mx: '2px',
                        },
                        '& .MuiPickersDay-root.Mui-disabled': {
                          color: colors.text,
                          opacity: 0.2,
                        },
                        '& .MuiPickersDay-root:not(.Mui-disabled)': {
                          fontWeight: 600,
                          bgcolor: 'transparent',
                          '&:hover': {
                            bgcolor: colors.bgInput,
                            transform: 'scale(1.08)',
                          },
                        },
                        '& .MuiPickersDay-root.Mui-selected': {
                          bgcolor: `${colors.info} !important`,
                          color: '#fff !important',
                          fontWeight: 700,
                          '&:hover': {
                            bgcolor: `${colors.info} !important`,
                          },
                        },
                        '& .MuiPickersDay-today': {
                          border: 'none !important',
                          position: 'relative',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: 2,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 4,
                            height: 4,
                            borderRadius: '50%',
                            bgcolor: colors.info,
                          },
                        },
                        '& .MuiDayCalendar-root': {
                          bgcolor: 'transparent',
                          backdropFilter: 'none',
                        },
                        '& .MuiDayCalendar-slideTransition': {
                          minHeight: 230,
                          bgcolor: 'transparent',
                          backdropFilter: 'none',
                        },
                        '& .MuiPickersFadeTransitionGroup-root': {
                          bgcolor: 'transparent',
                          backdropFilter: 'none',
                        },
                        '& .MuiDateCalendar-viewTransitionContainer': {
                          bgcolor: 'transparent',
                          backdropFilter: 'none',
                        },
                        '& .MuiPickersSlideTransition-root': {
                          overflow: 'hidden',
                          bgcolor: 'transparent',
                          backdropFilter: 'none',
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Box>
              )}

              {/* Step: Time Selection */}
              {step === 'time' && selectedDate && (
                <Box>
                  <Box className="flex items-center gap-2 mb-3">
                    <Button
                      size="small"
                      startIcon={<ArrowBackIcon />}
                      onClick={() => setStep('date')}
                      sx={{ color: colors.text }}
                    >
                      {t('Back')}
                    </Button>
                    <Box className="flex items-center gap-1">
                      <CalendarMonthIcon
                        sx={{ fontSize: 18, color: colors.text, opacity: 0.7 }}
                      />
                      <Typography
                        variant="h6"
                        sx={{ color: colors.text }}
                      >
                        {selectedDate.format('dd, DD.MM.YYYY')}
                      </Typography>
                    </Box>
                  </Box>
                  <Box className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {(slots[selectedDate.format('YYYY-MM-DD')] || []).map(
                      (slot) => (
                        <Button
                          key={slot}
                          variant="outlined"
                          onClick={() => handleSlotSelect(slot)}
                          sx={{
                            color: colors.text,
                            borderColor: colors.bgInput,
                            bgcolor: colors.bgInput,
                            fontWeight: 600,
                            '&:hover': {
                              bgcolor: colors.selected,
                              borderColor: colors.selected,
                            },
                          }}
                        >
                          {slot}
                        </Button>
                      )
                    )}
                  </Box>
                </Box>
              )}

              {/* Step: Location Selection */}
              {step === 'location' && (
                <Box>
                  <Box className="flex items-center gap-2 mb-3">
                    <Button
                      size="small"
                      startIcon={<ArrowBackIcon />}
                      onClick={() => setStep('time')}
                      sx={{ color: colors.text }}
                    >
                      {t('Back')}
                    </Button>
                    <Box className="flex items-center gap-1">
                      <CalendarMonthIcon
                        sx={{ fontSize: 18, color: colors.text, opacity: 0.7 }}
                      />
                      <Typography variant="h6" sx={{ color: colors.text }}>
                        {moment(selectedSlot).format('dd, DD.MM.YYYY')}{' '}
                        {t('at')} {moment(selectedSlot).format('HH:mm')}{' '}
                        {t('Clock')}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography
                    variant="h6"
                    className="text-center mb-3"
                    sx={{ color: colors.text }}
                  >
                    {t('Select Location')}
                  </Typography>
                  <Box className="flex flex-col gap-2">
                    {bookingConfig.offerOnline && (
                      <Button
                        variant="outlined"
                        startIcon={<VideocamIcon />}
                        onClick={() => handleLocationSelect('online', null)}
                        sx={{
                          color: colors.text,
                          borderColor: colors.bgInput,
                          bgcolor: colors.bgInput,
                          fontWeight: 600,
                          py: 1.5,
                          fontSize: '1rem',
                          justifyContent: 'flex-start',
                          '&:hover': {
                            bgcolor: colors.selected,
                            borderColor: colors.selected,
                          },
                        }}
                      >
                        {t('Online Meeting')}
                      </Button>
                    )}
                    {(bookingConfig.locations || []).map((loc, index) => (
                      <Button
                        key={index}
                        variant="outlined"
                        startIcon={<LocationOnIcon />}
                        onClick={() => handleLocationSelect('onsite', index)}
                        sx={{
                          color: colors.text,
                          borderColor: colors.bgInput,
                          bgcolor: colors.bgInput,
                          fontWeight: 600,
                          py: 1.5,
                          fontSize: '1rem',
                          justifyContent: 'flex-start',
                          textAlign: 'left',
                          '&:hover': {
                            bgcolor: colors.selected,
                            borderColor: colors.selected,
                          },
                        }}
                      >
                        <Box>
                          <Box>{loc.label}</Box>
                          <Typography
                            variant="caption"
                            sx={{ opacity: 0.6, fontWeight: 400 }}
                          >
                            {loc.street} {loc.house_number}, {loc.zip_code}{' '}
                            {loc.city}
                          </Typography>
                        </Box>
                      </Button>
                    ))}
                  </Box>
                </Box>
              )}

              {/* Step: Form */}
              {step === 'form' && (
                <Box>
                  <Box className="flex items-center gap-2 mb-3">
                    <Button
                      size="small"
                      startIcon={<ArrowBackIcon />}
                      onClick={() => {
                        const hasOnline = bookingConfig.offerOnline;
                        const locations = bookingConfig.locations || [];
                        const hasOnsite = locations.length > 0;
                        // Go back to location step if it was shown, otherwise time
                        if ((hasOnline && hasOnsite) || (!hasOnline && locations.length > 1)) {
                          setStep('location');
                        } else {
                          setStep('time');
                        }
                      }}
                      sx={{ color: colors.text }}
                    >
                      {t('Back')}
                    </Button>
                    <Box className="flex items-center gap-1">
                      <CalendarMonthIcon
                        sx={{
                          fontSize: 18,
                          color: colors.text,
                          opacity: 0.7,
                        }}
                      />
                      <Typography variant="h6" sx={{ color: colors.text }}>
                        {moment(selectedSlot).format('dd, DD.MM.YYYY')}{' '}
                        {t('at')} {moment(selectedSlot).format('HH:mm')}{' '}
                        {t('Clock')}
                      </Typography>
                    </Box>
                  </Box>

                  {formErrors._global && (
                    <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                      {formErrors._global}
                    </Typography>
                  )}

                  <Box className="flex flex-col gap-3">
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      sx={{ color: colors.text }}
                    >
                      {t('Company')}
                    </Typography>
                    <TextField
                      label={t('Company Name') + ' *'}
                      value={formData.company_name}
                      onChange={(e) =>
                        handleFormChange('company_name', e.target.value)
                      }
                      error={!!formErrors.company_name}
                      helperText={formErrors.company_name}
                      size="small"
                      fullWidth
                    />
                    <Box className="flex gap-2">
                      <TextField
                        label={t('Street') + ' *'}
                        value={formData.street}
                        onChange={(e) =>
                          handleFormChange('street', e.target.value)
                        }
                        error={!!formErrors.street}
                        helperText={formErrors.street}
                        size="small"
                        fullWidth
                      />
                      <TextField
                        label={t('House Nr.') + ' *'}
                        value={formData.house_number}
                        onChange={(e) =>
                          handleFormChange('house_number', e.target.value)
                        }
                        error={!!formErrors.house_number}
                        helperText={formErrors.house_number}
                        size="small"
                        sx={{ width: '120px', minWidth: '120px' }}
                      />
                    </Box>
                    <Box className="flex gap-2">
                      <TextField
                        label={t('ZIP Code') + ' *'}
                        value={formData.zip_code}
                        onChange={(e) =>
                          handleFormChange('zip_code', e.target.value)
                        }
                        error={!!formErrors.zip_code}
                        helperText={formErrors.zip_code}
                        size="small"
                        sx={{ width: '120px', minWidth: '120px' }}
                      />
                      <TextField
                        label={t('City') + ' *'}
                        value={formData.city}
                        onChange={(e) =>
                          handleFormChange('city', e.target.value)
                        }
                        error={!!formErrors.city}
                        helperText={formErrors.city}
                        size="small"
                        fullWidth
                      />
                    </Box>
                    <TextField
                      label={`${t('Employee Count')} (${t('optional')})`}
                      value={formData.employee_count}
                      onChange={(e) =>
                        handleFormChange('employee_count', e.target.value)
                      }
                      size="small"
                      type="number"
                      fullWidth
                    />

                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      sx={{ mt: 1, color: colors.text }}
                    >
                      {t('Contact Person')}
                    </Typography>
                    <Select
                      value={formData.salutation}
                      onChange={(e) =>
                        handleFormChange('salutation', e.target.value)
                      }
                      size="small"
                      fullWidth
                    >
                      <MenuItem value="mr">{t('Mr.')}</MenuItem>
                      <MenuItem value="mrs">{t('Mrs.')}</MenuItem>
                      <MenuItem value="diverse">{t('Diverse')}</MenuItem>
                    </Select>
                    <Box className="flex gap-2">
                      <TextField
                        label={t('First Name') + ' *'}
                        value={formData.first_name}
                        onChange={(e) =>
                          handleFormChange('first_name', e.target.value)
                        }
                        error={!!formErrors.first_name}
                        helperText={formErrors.first_name}
                        size="small"
                        fullWidth
                      />
                      <TextField
                        label={t('Last Name') + ' *'}
                        value={formData.last_name}
                        onChange={(e) =>
                          handleFormChange('last_name', e.target.value)
                        }
                        error={!!formErrors.last_name}
                        helperText={formErrors.last_name}
                        size="small"
                        fullWidth
                      />
                    </Box>
                    <TextField
                      label={t('E-Mail') + ' *'}
                      value={formData.email}
                      onChange={(e) =>
                        handleFormChange('email', e.target.value)
                      }
                      error={!!formErrors.email}
                      helperText={formErrors.email}
                      size="small"
                      type="email"
                      fullWidth
                    />
                    <TextField
                      label={t('Phone') + ' *'}
                      value={formData.phone}
                      onChange={(e) =>
                        handleFormChange('phone', e.target.value)
                      }
                      error={!!formErrors.phone}
                      helperText={formErrors.phone}
                      size="small"
                      fullWidth
                    />

                    <TextField
                      label={`${t('Concern')} (${t('optional')})`}
                      value={formData.concern}
                      onChange={(e) =>
                        handleFormChange('concern', e.target.value)
                      }
                      size="small"
                      fullWidth
                      multiline
                      minRows={2}
                      maxRows={5}
                      inputProps={{ maxLength: 2000 }}
                    />

                    {/* Honeypot - hidden from users */}
                    <Box sx={{ position: 'absolute', left: '-9999px' }}>
                      <TextField
                        label="Website URL"
                        value={formData.website_url}
                        onChange={(e) =>
                          handleFormChange('website_url', e.target.value)
                        }
                        tabIndex={-1}
                        autoComplete="off"
                      />
                    </Box>

                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={formData.privacy_accepted}
                          onChange={(e) =>
                            handleFormChange(
                              'privacy_accepted',
                              e.target.checked
                            )
                          }
                          sx={{
                            color: colors.text,
                            '&.Mui-checked': {
                              color: colors.info,
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          variant="body2"
                          sx={{ color: colors.text }}
                        >
                          {t('I accept the')}{' '}
                          <a
                            href="/privacy-policy"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              textDecoration: 'underline',
                              color: colors.info,
                            }}
                          >
                            {t('Privacy Policy')}
                          </a>{' '}
                          *
                        </Typography>
                      }
                    />
                    {formErrors.privacy_accepted && (
                      <Typography color="error" variant="caption">
                        {formErrors.privacy_accepted}
                      </Typography>
                    )}

                    <Button
                      variant="contained"
                      color="success"
                      onClick={handleSubmit}
                      disabled={submitLoading}
                      sx={{ mt: 1, mb: 2, py: 1.5, fontWeight: 600 }}
                    >
                      <Loader active={submitLoading} />
                      {t('Book Appointment')}
                    </Button>
                  </Box>
                </Box>
              )}

              {/* Step: Confirmation */}
              {step === 'confirmation' && confirmation && (
                <Box className="flex flex-col items-center gap-4 py-8">
                  <CheckCircleOutlineIcon
                    color="success"
                    sx={{ fontSize: 64 }}
                  />
                  <Typography
                    variant="h5"
                    fontWeight={600}
                    sx={{ color: colors.text }}
                  >
                    {t('Booking Confirmed!')}
                  </Typography>
                  <Box
                    className="text-center px-6 py-4 rounded-lg"
                    sx={{ bgcolor: colors.bgInput }}
                  >
                    <Typography variant="body1" sx={{ color: colors.text }}>
                      {moment(confirmation.date).format('dd, DD.MM.YYYY')}
                    </Typography>
                    <Typography
                      variant="h4"
                      fontWeight={600}
                      sx={{ color: colors.text }}
                    >
                      {confirmation.time} {t('Clock')}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ opacity: 0.7, color: colors.text }}
                    >
                      {confirmation.duration} {t('Minutes')}
                    </Typography>
                  </Box>
                  <Typography
                    variant="body2"
                    sx={{ opacity: 0.7, color: colors.text }}
                  >
                    {t(
                      'A confirmation email has been sent to your address.'
                    )}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Div100vh>
  );
};

export default PublicBooking;
